import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star } from 'lucide-react';
import { Button } from './ui/button';

const CATEGORY_VIDEOS = {
    distance: '/videos/electric_dirtbike.mp4',
    speed: '/videos/gas_dirtbike.mp4',
    streak: '/videos/skateboard.mp4',
    airdrop: '/videos/electric_longboard.mp4',
    endurance: '/videos/euc.mp4',
    default: '/videos/surfer.mp4',
};

const PARTICLES = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 3,
    size: 4 + Math.random() * 8,
    color: ['#1E90FF', '#FF33CC', '#FFD700', '#00BFFF', '#00E64D'][Math.floor(Math.random() * 5)],
}));

export default function ChallengeComplete({ challenge, reward, onClose }) {
    const videoRef = useRef(null);
    const [phase, setPhase] = useState('intro'); // intro -> slowmo -> reward -> done
    const [videoReady, setVideoReady] = useState(false);

    const videoSrc = CATEGORY_VIDEOS[challenge?.category] || CATEGORY_VIDEOS.default;

    useEffect(() => {
        // Phase timeline
        const t1 = setTimeout(() => setPhase('slowmo'), 500);
        const t2 = setTimeout(() => setPhase('reward'), 3500);
        const t3 = setTimeout(() => setPhase('done'), 5000);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, []);

    useEffect(() => {
        if (!videoRef.current || !videoReady) return;
        if (phase === 'slowmo') {
            videoRef.current.playbackRate = 0.3;
        } else if (phase === 'reward') {
            videoRef.current.playbackRate = 1.2;
        }
    }, [phase, videoReady]);

    const handleVideoLoad = () => {
        setVideoReady(true);
        if (videoRef.current) {
            videoRef.current.playbackRate = 0.3;
        }
    };

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center"
            data-testid="challenge-complete-modal"
        >
            {/* Video Background */}
            <video
                ref={videoRef}
                src={videoSrc}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                playsInline
                loop
                onCanPlay={handleVideoLoad}
                style={{ opacity: videoReady ? 1 : 0, transition: 'opacity 0.5s' }}
            />

            {/* Dark vignette overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30 z-10" />
            <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_30%,rgba(0,0,0,0.8)_100%)] z-10" />

            {/* Floating particles */}
            <div className="absolute inset-0 z-20 overflow-hidden pointer-events-none">
                {phase !== 'intro' && PARTICLES.map((p) => (
                    <motion.div
                        key={p.id}
                        className="absolute rounded-full"
                        style={{
                            left: `${p.x}%`,
                            width: p.size,
                            height: p.size,
                            backgroundColor: p.color,
                            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
                        }}
                        initial={{ bottom: '-10%', opacity: 0 }}
                        animate={{ bottom: '110%', opacity: [0, 1, 1, 0] }}
                        transition={{
                            duration: p.duration,
                            delay: p.delay,
                            repeat: Infinity,
                            ease: 'linear',
                        }}
                    />
                ))}
            </div>

            {/* Content */}
            <div className="relative z-30 text-center px-6 max-w-lg">
                {/* Slow-mo badge */}
                {phase === 'slowmo' && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mb-8"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hot-pink/20 border border-hot-pink/30 backdrop-blur-md">
                            <div className="w-2 h-2 rounded-full bg-hot-pink animate-pulse" />
                            <span className="text-hot-pink text-sm font-display tracking-[0.2em] uppercase">
                                Challenge Complete
                            </span>
                        </div>
                    </motion.div>
                )}

                {/* Trophy icon */}
                {(phase === 'reward' || phase === 'done') && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0, rotate: -20 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                        className="mb-6"
                    >
                        <div className="w-24 h-24 mx-auto bg-neon-gold/20 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(255,215,0,0.4)] border border-neon-gold/30">
                            <Trophy className="w-12 h-12 text-neon-gold drop-shadow-[0_0_10px_rgba(255,215,0,0.6)]" />
                        </div>
                    </motion.div>
                )}

                {/* Challenge title */}
                {(phase === 'reward' || phase === 'done') && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <h2 className="text-3xl sm:text-5xl font-black font-display gradient-text mb-3" data-testid="completion-title">
                            {challenge?.title || 'CHALLENGE'}
                        </h2>
                        <p className="text-white/60 text-sm font-display tracking-[0.15em] uppercase">
                            Completed
                        </p>
                    </motion.div>
                )}

                {/* Reward */}
                {(phase === 'reward' || phase === 'done') && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, type: 'spring', stiffness: 150 }}
                        className="mt-8"
                    >
                        <div className="inline-flex flex-col items-center gap-2 bg-black/40 backdrop-blur-xl rounded-2xl px-10 py-6 border border-neon-gold/20 shadow-[0_0_30px_rgba(255,215,0,0.15)]">
                            <span className="text-xs text-neon-gold/70 font-display tracking-[0.3em] uppercase">
                                Reward Collected
                            </span>
                            <div className="flex items-baseline gap-2">
                                <span className="text-5xl sm:text-7xl font-black font-display text-neon-gold text-glow-gold" data-testid="completion-reward">
                                    +{reward?.rmr || challenge?.reward_boltz || 0}
                                </span>
                                <span className="text-2xl font-bold font-display text-neon-gold/70">
                                    Boltz
                                </span>
                            </div>
                            {(reward?.xp || challenge?.reward_xp) > 0 && (
                                <div className="flex items-center gap-1 mt-1 text-primary">
                                    <Star className="w-4 h-4" />
                                    <span className="text-sm font-mono font-bold">
                                        +{reward?.xp || challenge?.reward_xp} XP
                                    </span>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* Continue button */}
                {phase === 'done' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mt-8"
                    >
                        <Button
                            onClick={onClose}
                            className="px-10 py-6 rounded-full bg-gradient-to-r from-primary to-cyan-glow text-white font-bold text-lg hover:scale-105 hover:shadow-[0_0_30px_rgba(30,144,255,0.5)] transition-all duration-300"
                            style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.05em' }}
                            data-testid="completion-continue-btn"
                        >
                            Continue
                        </Button>
                    </motion.div>
                )}
            </div>

            {/* Edge glow lines */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-neon-gold to-transparent z-30 opacity-60" />
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent z-30 opacity-40" />
        </div>
    );
}
